import { useNavigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ShieldCheck, Layers, MousePointerClick, Zap, Lock, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: Sparkles, tag: "Core Feature", color: "text-primary bg-primary/10", title: "AI Content Generation",
    desc: "Describe your learning objective — our AI builds a complete VR scene with 3D assets, interactions, and narrative flow in minutes." },
  { icon: ShieldCheck, tag: "Compliance", color: "text-emerald-600 bg-emerald-500/10", title: "Standards Alignment",
    desc: "Every lesson is mapped to Common Core, state-specific standards, and NGSS so you can trust the coverage and prove it to administrators." },
  { icon: Layers, tag: "K–12 Coverage", color: "text-cyan-600 bg-cyan-500/10", title: "Multi-Subject Coverage",
    desc: "From molecular chemistry to the French Revolution — generate VR lessons across STEM, history, and language arts without switching tools." },
  { icon: MousePointerClick, tag: "Educator-First", color: "text-accent bg-accent/10", title: "No-Code Builder",
    desc: "Educators with no technical background can customize scenes, adjust difficulty, and add quizzes using an intuitive drag-and-drop interface." },
  { icon: Zap, tag: "Speed", color: "text-amber-600 bg-amber-500/10", title: "Rapid Production",
    desc: "What used to take a production team months now takes a single teacher an afternoon — reduce lesson development time by up to 90%." },
  { icon: Lock, tag: "Enterprise", color: "text-slate-700 bg-slate-500/10", title: "District-Ready Security",
    desc: "SOC 2 compliant, COPPA-aware, and FERPA-aligned. Role-based access control and audit logs for school district procurement." },
];

const STANDARDS = [
  { code: "CCSS", name: "Common Core State Standards", area: "Math, ELA, Literacy" },
  { code: "NGSS", name: "Next Generation Science Standards", area: "Science (K–12)" },
  { code: "NCSS", name: "National Council for Social Studies", area: "History, Geography, Civics" },
  { code: "ACTFL", name: "World Language Standards", area: "Spanish, French, German" },
];

export default function Features() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-3xl mx-auto animate-in-up">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Platform Capabilities</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            Everything you need to bring VR to the classroom
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            A complete end-to-end platform for educators and administrators — from AI lesson generation to student
            progress tracking. VividVR ships generation, standards tagging, and rollout under one login.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().split(" ")[0]}`} className="rounded-3xl border border-border bg-card p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${f.color}`}><f.icon className="w-6 h-6" /></div>
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{f.tag}</Badge>
              </div>
              <h3 className="text-xl font-display font-semibold mt-5">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Standards compliance */}
        <div className="mt-16">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Standards Compliance</div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Every lesson is pre-mapped to your standards</h2>
            <p className="mt-3 text-muted-foreground">
              VividVR automatically tags each AI-generated lesson with the exact standards it addresses — eliminating the
              compliance work that makes most VR adoption projects stall before they start.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {STANDARDS.map((s) => (
              <div key={s.code} className="rounded-2xl border border-border bg-card p-5">
                <div className="font-mono font-bold text-primary text-lg">{s.code}</div>
                <div className="font-semibold text-sm mt-1">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.area}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14 mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">See it build a lesson from your prompt</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="features-generate-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100">
              Generate a lesson <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => nav("/how-it-works")} className="rounded-full h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10">
              See how it works
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
